import React from "react";
import { useTranslation } from "react-i18next";
import { PaymentMethodSummary } from "../../api/services";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const formatBrand = (brand: string | null, fallback: string) =>
  brand
    ? brand.split(/[_\s]/).map(capitalize).join(" ")
    : fallback;

const formatAccountType = (s: string | null) =>
  s ? capitalize(s) : "";

interface Props {
  method: PaymentMethodSummary;
  onSetDefault: (id: number) => void;
  onRemove: (id: number) => void;
  /** Called to authorize ACH for a bank account that lacks a mandate. */
  onAddMandate?: (id: number) => void;
  busy: boolean;
  /** When true, the Remove button is disabled (e.g. last bank account). */
  disableRemove?: boolean;
}

export const PaymentMethodRow: React.FC<Props> = ({
  method,
  onSetDefault,
  onRemove,
  onAddMandate,
  busy,
  disableRemove = false,
}) => {
  const { t } = useTranslation();
  const isCard = method.type === "card";
  const requiresMandate = method.requiresMandate;
  return (
    <div
      className={`rounded-xl border bg-white p-5 flex flex-col h-full ${
        requiresMandate ? "border-warning bg-amber-50/40" : "border-gray-200"
      }`}
    >
      <div className="flex items-center mb-4">
        <div className="mr-3 text-[2rem]">{isCard ? "💳" : "🏦"}</div>
        <div className="grow">
          {isCard ? (
            <>
              <h6 className="text-sm font-semibold mb-1">
                {formatBrand(method.brand, t("paymentMethodPicker.card"))}
                {method.expMonth && method.expYear && (
                  <span className="text-xs font-normal text-muted ms-2">
                    {t("paymentMethodPicker.exp", {
                      month: String(method.expMonth).padStart(2, "0"),
                      year: String(method.expYear).slice(-2),
                    })}
                  </span>
                )}
              </h6>
              <p className="text-muted text-sm">•••• •••• •••• {method.last4}</p>
            </>
          ) : (
            <>
              <h6 className="text-sm font-semibold mb-1">
                {method.bankName || t("paymentMethodPicker.bankAccount")}
              </h6>
              <p className="text-muted text-sm">
                {formatAccountType(method.accountType)} ••••{method.last4}
              </p>
            </>
          )}
        </div>
        {method.isDefault && (
          <span className="rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-medium text-success">
            {t("paymentMethodPicker.default")}
          </span>
        )}
      </div>

      {requiresMandate && (
        <p className="mb-3 text-xs font-medium text-warning">
          ⚠{" "}
          {t(
            "paymentMethodRow.needsAuthorization",
            "This bank account needs ACH authorization before it can be used.",
          )}
        </p>
      )}

      <div className="mt-auto flex gap-2">
        {requiresMandate && onAddMandate && (
          <button
            type="button"
            className="rounded-md bg-warning px-3 py-1.5 text-xs font-medium text-black transition-colors hover:brightness-95 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy}
            onClick={() => onAddMandate(method.id)}
          >
            {t("paymentMethodRow.authorizeAch", "Authorize ACH")}
          </button>
        )}
        {!method.isDefault && (
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            disabled={busy}
            onClick={() => onSetDefault(method.id)}
          >
            {t("paymentMethodRow.setDefault")}
          </button>
        )}
        <button
          type="button"
          className="rounded-md border border-danger px-3 py-1.5 text-xs font-medium text-danger transition-colors hover:bg-red-50 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy || disableRemove}
          title={
            disableRemove
              ? t(
                  "paymentMethodRow.lastBankHint",
                  "You must keep at least one bank account on file."
                )
              : undefined
          }
          onClick={() => onRemove(method.id)}
        >
          {t("paymentMethodRow.remove")}
        </button>
      </div>
    </div>
  );
};

export default PaymentMethodRow;
